import { SVTable } from '@/components/core';
import { MESSAGES } from '@/constants/messages';
import { useDownloadPresignedUrl } from '@/hooks/requests/usePresignedUrls';
import { BankApplicationDocument } from '@/types/bankApplications';
import { SVTableRow } from '@/types/common';
import showToast from '@/utils/toast';
import { CloudDownload } from '@mui/icons-material';
import { Card, IconButton, Typography } from '@mui/material';

type BankApplicationDocumentsProps = {
  documents: BankApplicationDocument[];
  uuid: string;
};

export default function BankApplicationDocuments({
  documents,
  uuid,
}: BankApplicationDocumentsProps) {
  const { downloadPresignedUrl } = useDownloadPresignedUrl();

  if (!documents || documents.length === 0) {
    return null;
  }

  async function onDownloadDocument(file: string) {
    const url = new URL(file);
    try {
      const response = await downloadPresignedUrl({
        type: 'external bank account',
        uuid,
        file: url.pathname.slice(1),
      });
      window.open(response.url, '_blank');
    } catch {
      showToast({
        type: 'error',
        data: {
          content: MESSAGES.genericError,
        },
      });
    }
  }

  const rows: SVTableRow[] = documents.map(document => {
    const { name, side, url } = document;

    return {
      key: `documentRow${url}`,
      cells: [
        {
          content: name,
          type: 'text',
          key: `filename${url}`,
        },
        {
          content: side,
          type: 'text',
          key: `type${url}`,
        },
        {
          content: (
            <IconButton onClick={() => onDownloadDocument(url)}>
              <CloudDownload />
            </IconButton>
          ),
          type: 'custom',
          key: `download${url}`,
        },
      ],
    };
  });

  return (
    <Card>
      <Typography variant="h5" mb={2}>
        Documents
      </Typography>
      <SVTable
        ariaLabel="Documents"
        headers={[
          {
            content: 'Name',
            key: 'name',
          },
          { content: 'Side', key: 'side' },
          {
            content: 'Download',
            key: 'download',
          },
        ]}
        rows={rows}
      />
    </Card>
  );
}
